import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Switch,
  TextField,
  Divider,
  Button,
  FormControlLabel,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { API_BASE_URL } from "../../services/api/client";
import ImageIcon from "@mui/icons-material/Image";
import { lightTheme } from "../../styles/theme";
import {
  fileToDataUrl,
  processImageForUpload,
  PUBLIC_PAGE_IMAGE_UPLOAD_OPTIONS,
  shouldProcessNoticeImage,
} from "../../utils/imageUpload";
import { useSnackbar } from "../../context/SnackbarContext";

interface PublicPageSettings {
  tagline?: string;
  showPhone?: boolean;
  showEmail?: boolean;
  showWebsite?: boolean;
  showShopPosts?: boolean;
  showParentCount?: boolean;
  showActiveNoticeCount?: boolean;
  showUpcomingEventsCount?: boolean;
  themeColorHex?: string;
  descriptionImageUrl?: string;
  storeImageUrl?: string;
}

interface PublicPageSettingsPanelProps {
  user: {
    isPublic?: string | boolean;
    publicPageSettings?: PublicPageSettings;
    soid?: string;
    [key: string]: any; // Allow other user properties
  };
  canEdit: boolean;
  isUpdating: boolean;
  isUpdatingPublicPage?: boolean;
  onUpdate: (settings: PublicPageSettings) => Promise<void>;
  onUpdatePublicPage?: (isPublic: boolean) => Promise<void>;
}

const PublicPageSettingsPanel: React.FC<PublicPageSettingsPanelProps> = ({
  user,
  canEdit,
  isUpdating,
  isUpdatingPublicPage,
  onUpdate,
  onUpdatePublicPage,
}) => {
  const { showMessage } = useSnackbar();
  const currentSettings = user.publicPageSettings || {};
  
  // Single source of truth for all settings
  const [settings, setSettings] = useState<PublicPageSettings>(() => ({
    showPhone: currentSettings.showPhone ?? true,
    showEmail: currentSettings.showEmail ?? true,
    showWebsite: currentSettings.showWebsite ?? true,
    showShopPosts: currentSettings.showShopPosts ?? true,
    showParentCount: currentSettings.showParentCount ?? true,
    showActiveNoticeCount: currentSettings.showActiveNoticeCount ?? true,
    showUpcomingEventsCount: currentSettings.showUpcomingEventsCount ?? true,
    themeColorHex: currentSettings.themeColorHex || lightTheme.colors.primary,
    tagline: currentSettings.tagline || "",
    descriptionImageUrl: currentSettings.descriptionImageUrl,
    storeImageUrl: currentSettings.storeImageUrl,
  }));

  const [isUpdatingLocal, setIsUpdatingLocal] = useState(false);
  const [isUploadingDescriptionImage, setIsUploadingDescriptionImage] = useState(false);
  const [isUploadingStoreImage, setIsUploadingStoreImage] = useState(false);
  const [isSavingBasicSettings, setIsSavingBasicSettings] = useState(false);
  const [hasBasicSettingsChanges, setHasBasicSettingsChanges] = useState(false);
  const descriptionImageInputRef = useRef<HTMLInputElement>(null);
  const storeImageInputRef = useRef<HTMLInputElement>(null);
  const { token } = useAuth();
  // Shop settings only matter to orgs with Ad Center; the public page hides
  // the Shop section for everyone else.
  const { isAdCenterEnabled } = useFeatureFlags();

  // Update local state when user.publicPageSettings changes (but not while we're updating locally)
  useEffect(() => {
    if (!isUpdatingLocal && user.publicPageSettings) {
      const newSettings: PublicPageSettings = {
        showPhone: user.publicPageSettings.showPhone ?? true,
        showEmail: user.publicPageSettings.showEmail ?? true,
        showWebsite: user.publicPageSettings.showWebsite ?? true,
        showShopPosts: user.publicPageSettings.showShopPosts ?? true,
        showParentCount: user.publicPageSettings.showParentCount ?? true,
        showActiveNoticeCount: user.publicPageSettings.showActiveNoticeCount ?? true,
        showUpcomingEventsCount: user.publicPageSettings.showUpcomingEventsCount ?? true,
        themeColorHex: user.publicPageSettings.themeColorHex || lightTheme.colors.primary,
        tagline: user.publicPageSettings.tagline || "",
        descriptionImageUrl: user.publicPageSettings.descriptionImageUrl,
        storeImageUrl: user.publicPageSettings.storeImageUrl,
      };
      setSettings(newSettings);
    }
  }, [user.publicPageSettings, isUpdatingLocal]);

  const handleToggle = useCallback(
    (key: keyof PublicPageSettings) => {
      setIsUpdatingLocal(true);
      setSettings((prevSettings) => {
        const newSettings = {
          ...prevSettings,
          [key]: !prevSettings[key],
        };
        // Update backend asynchronously
        onUpdate(newSettings).finally(() => {
          setIsUpdatingLocal(false);
        });
        return newSettings;
      });
    },
    [onUpdate]
  );

  const handleTaglineChange = useCallback(
    (value: string) => {
      setSettings((prevSettings) => {
        const newSettings = {
          ...prevSettings,
          tagline: value,
        };
        // Check if this is different from the original value
        const originalTagline = user.publicPageSettings?.tagline || "";
        const originalColor = user.publicPageSettings?.themeColorHex || lightTheme.colors.primary;
        setHasBasicSettingsChanges(
          newSettings.tagline !== originalTagline || 
          newSettings.themeColorHex !== originalColor
        );
        return newSettings;
      });
    },
    [user.publicPageSettings]
  );

  const handleColorChange = useCallback(
    (color: string) => {
      setSettings((prevSettings) => {
        const newSettings = {
          ...prevSettings,
          themeColorHex: color,
        };
        // Check if this is different from the original value
        const originalTagline = user.publicPageSettings?.tagline || "";
        const originalColor = user.publicPageSettings?.themeColorHex || lightTheme.colors.primary;
        setHasBasicSettingsChanges(
          newSettings.tagline !== originalTagline || 
          newSettings.themeColorHex !== originalColor
        );
        return newSettings;
      });
    },
    [user.publicPageSettings]
  );

  const handleSaveBasicSettings = useCallback(async () => {
    if (!hasBasicSettingsChanges) return;
    
    setIsSavingBasicSettings(true);
    setIsUpdatingLocal(true);
    
    try {
      // Merge with existing settings to preserve other values
      await onUpdate({
        ...settings,
        tagline: settings.tagline,
        themeColorHex: settings.themeColorHex,
      });
      setHasBasicSettingsChanges(false);
    } catch {
      // Error surfaced via parent onUpdate / snackbar.
    } finally {
      setIsSavingBasicSettings(false);
      setIsUpdatingLocal(false);
    }
  }, [hasBasicSettingsChanges, settings, onUpdate]);

  // Reset hasBasicSettingsChanges when user.publicPageSettings changes
  useEffect(() => {
    if (!isUpdatingLocal && user.publicPageSettings) {
      const originalTagline = user.publicPageSettings.tagline || "";
      const originalColor = user.publicPageSettings.themeColorHex || lightTheme.colors.primary;
      setHasBasicSettingsChanges(
        (settings.tagline || "") !== originalTagline || 
        (settings.themeColorHex || lightTheme.colors.primary) !== originalColor
      );
    }
  }, [user.publicPageSettings, isUpdatingLocal, settings.tagline, settings.themeColorHex]);

  const handleImageUpload = useCallback(
    async (file: File, imageType: "descriptionImageUrl" | "storeImageUrl") => {
      if (!file || !file.type.startsWith("image/")) {
        showMessage({
          message: "Please select an image file.",
          severity: "warning",
        });
        return;
      }

      if (imageType === "descriptionImageUrl") {
        setIsUploadingDescriptionImage(true);
      } else {
        setIsUploadingStoreImage(true);
      }

      try {
        let fileToUpload = file;
        if (shouldProcessNoticeImage(file)) {
          try {
            fileToUpload = await processImageForUpload(
              file,
              PUBLIC_PAGE_IMAGE_UPLOAD_OPTIONS
            );
          } catch {
            // Upload original if optimization fails.
          }
        }

        const base64Image = await fileToDataUrl(fileToUpload);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }

        // Upload image directly via the account update endpoint
        // The backend will handle S3 upload and store URL in publicPageSettings
        const uploadPayload: any = {
          [imageType === "descriptionImageUrl" ? "descriptionImage" : "storeImage"]: base64Image,
          imageContentType: fileToUpload.type,
        };

        const response = await fetch(`${API_BASE_URL}/v1/org/${schoolId}/account`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(uploadPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to upload image: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        const imageUrl = imageType === "descriptionImageUrl" 
          ? data.organization?.publicPageSettings?.descriptionImageUrl
          : data.organization?.publicPageSettings?.storeImageUrl;

        if (!imageUrl) {
          throw new Error("Image URL not returned from server");
        }

        // Update settings with the new image URL using functional update
        setIsUpdatingLocal(true);
        setSettings((prevSettings) => {
          const newSettings = {
            ...prevSettings,
            [imageType]: imageUrl,
          };
          // Update backend asynchronously
          onUpdate(newSettings).finally(() => {
            setIsUpdatingLocal(false);
          });
          return newSettings;
        });
      } catch {
        showMessage({
          message: "Failed to upload image. Please try again.",
          severity: "error",
        });
        setIsUpdatingLocal(false);
      } finally {
        if (imageType === "descriptionImageUrl") {
          setIsUploadingDescriptionImage(false);
        } else {
          setIsUploadingStoreImage(false);
        }
      }
    },
    [onUpdate, user, token, showMessage]
  );

  const handleDescriptionImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleImageUpload(file, "descriptionImageUrl");
      }
    },
    [handleImageUpload]
  );

  const handleStoreImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleImageUpload(file, "storeImageUrl");
      }
    },
    [handleImageUpload]
  );

  // Show a message if public page is not enabled yet
  // Note: isCircleOpen is a different feature for subcircles, not related to public web pages
  // Only check isPublic for the public web page feature
  const [isPublicPageEnabled, setIsPublicPageEnabled] = useState(
    user.isPublic === "true"
  );
  const [isToggling, setIsToggling] = useState(false);

  // Update local state when user.isPublic changes (but not while toggling to avoid race conditions)
  useEffect(() => {
    if (!isToggling) {
      setIsPublicPageEnabled(user.isPublic === "true");
    }
  }, [user.isPublic, isToggling]);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Public Website Settings
      </Typography>

      {canEdit && onUpdatePublicPage && (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={isPublicPageEnabled}
                onChange={async (e) => {
                  const newValue = e.target.checked;
                  const previousValue = isPublicPageEnabled;
                  
                  // Set toggling flag to prevent useEffect from overriding
                  setIsToggling(true);
                  // Optimistically update the UI
                  setIsPublicPageEnabled(newValue);
                  
                  try {
                    if (!onUpdatePublicPage) {
                      throw new Error("onUpdatePublicPage handler is not available");
                    }
                    
                    await onUpdatePublicPage(newValue);

                    // The user object will be updated by the updateUser call in handleUpdatePublicPage
                    // The useEffect will sync the state when user.isPublic changes
                    // No need to manually verify here as the useEffect handles it
                  } catch (error: any) {
                    setIsPublicPageEnabled(previousValue);
                    showMessage({
                      message:
                        error?.response?.data?.error ||
                        error?.message ||
                        "Failed to update public page setting.",
                      severity: "error",
                    });
                  } finally {
                    setTimeout(() => {
                      setIsToggling(false);
                    }, 800);
                  }
                }}
                disabled={isUpdatingPublicPage || isToggling}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Public Page
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Enable a public page for your organization that can be shared with anyone.
                  {user.soid && isPublicPageEnabled && (
                    <>
                      {" "}
                      <Typography
                        variant="caption"
                        component="a"
                        href={`https://hellonoticeboard.com/${user.soid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "primary.main",
                          textDecoration: "none",
                          fontWeight: 500,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        View public page →
                      </Typography>
                    </>
                  )}
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", mt: 1 }}
          />
        </Box>
      )}

      {!isPublicPageEnabled && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Enable the public page above to configure these settings.
          </Typography>
        </Box>
      )}

      {isPublicPageEnabled && (
        <>
          <Divider sx={{ my: 2 }} />
            
            {/* Basic Settings Section */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="600"
                  color="text.primary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}
                >
                  Basic Settings
                </Typography>
                {hasBasicSettingsChanges && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveBasicSettings}
                    disabled={!canEdit || isSavingBasicSettings || isUpdating}
                    sx={{
                      textTransform: "none",
                      px: 2,
                      py: 0.75,
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      backgroundColor: lightTheme.colors.primary,
                      "&:hover": {
                        backgroundColor: lightTheme.colors.primaryAccent,
                      },
                    }}
                  >
                    {isSavingBasicSettings ? "Saving..." : "Save Changes"}
                  </Button>
                )}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                }}
              >
                {/* Tagline */}
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    color="text.primary"
                    gutterBottom
                    sx={{ mb: 1.5 }}
                  >
                    Tagline
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={settings.tagline || ""}
                    onChange={(e) => handleTaglineChange(e.target.value)}
                    disabled={!canEdit || isUpdating || isSavingBasicSettings}
                    placeholder="e.g., Empowering Young Minds"
                    variant="outlined"
                  />
                </Box>

                {/* Theme Color */}
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    color="text.primary"
                    gutterBottom
                    sx={{ mb: 2 }}
                  >
                    Theme Color
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {[
                      lightTheme.colors.primary, // Teal (default)
                      lightTheme.colors.materialPurple300, // Light Purple
                      lightTheme.colors.eventBorder, // Green
                      lightTheme.colors.materialOrange800, // Orange
                      lightTheme.colors.red700, // Red
                      lightTheme.colors.materialBlue800, // Dark Blue
                      lightTheme.colors.amber700, // Yellow
                      lightTheme.colors.pureBlack, // Black
                    ].map((color) => (
                      <Box
                        key={color}
                        onClick={() => {
                          if (canEdit && !isUpdating && !isSavingBasicSettings) {
                            handleColorChange(color);
                          }
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "8px",
                          bgcolor: color,
                          border: (settings.themeColorHex || lightTheme.colors.primary) === color ? `3px solid ${lightTheme.colors.materialBlue700}` : `2px solid ${lightTheme.colors.borderLight}`,
                          cursor: canEdit && !isUpdating && !isSavingBasicSettings ? "pointer" : "default",
                          transition: "all 0.2s ease",
                          "&:hover": canEdit && !isUpdating && !isSavingBasicSettings ? {
                            transform: "scale(1.1)",
                            boxShadow: `0 2px 8px ${lightTheme.colors.shadowBlack15}`,
                          } : {},
                          opacity: !canEdit || isUpdating || isSavingBasicSettings ? 0.6 : 1,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Contact Information and Statistics - Side by Side */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 4,
              }}
            >
              {/* Contact Information Toggles */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="600"
                  color="text.primary"
                  gutterBottom
                  sx={{ mb: 2.5, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}
                >
                  Contact Information
                </Typography>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Switch
                      checked={settings.showPhone ?? true}
                      onChange={() => handleToggle("showPhone")}
                      disabled={!canEdit || isUpdating}
                      color="primary"
                      size="medium"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        Show Phone Number
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Display your phone number on the public page
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Switch
                      checked={settings.showEmail ?? true}
                      onChange={() => handleToggle("showEmail")}
                      disabled={!canEdit || isUpdating}
                      color="primary"
                      size="medium"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        Show Email Address
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Display your email address on the public page
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Switch
                      checked={settings.showWebsite ?? true}
                      onChange={() => handleToggle("showWebsite")}
                      disabled={!canEdit || isUpdating}
                      color="primary"
                      size="medium"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        Show Website Button
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Display a link to your website on the public page
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              {/* Statistics Toggles */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="600"
                  color="text.primary"
                  gutterBottom
                  sx={{ mb: 2.5, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}
                >
                  Statistics
                </Typography>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Switch
                      checked={settings.showParentCount ?? true}
                      onChange={() => handleToggle("showParentCount")}
                      disabled={!canEdit || isUpdating}
                      color="primary"
                      size="medium"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        Show Number of Parents
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Display the total number of connected parents
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Switch
                      checked={settings.showActiveNoticeCount ?? true}
                      onChange={() => handleToggle("showActiveNoticeCount")}
                      disabled={!canEdit || isUpdating}
                      color="primary"
                      size="medium"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        Show Active Notices Count
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Display the number of active notices
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Switch
                      checked={settings.showUpcomingEventsCount ?? true}
                      onChange={() => handleToggle("showUpcomingEventsCount")}
                      disabled={!canEdit || isUpdating}
                      color="primary"
                      size="medium"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        Show Upcoming Events Count
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Display the number of upcoming events
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Image Uploads Section */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight="600"
                color="text.primary"
                gutterBottom
                sx={{ mb: 2.5, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}
              >
                Custom Images
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                }}
              >
                {/* Description Image Upload */}
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    color="text.primary"
                    gutterBottom
                    sx={{ mb: 1.5 }}
                  >
                    Description Image
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    {settings.descriptionImageUrl && (
                      <Box
                        component="img"
                        src={settings.descriptionImageUrl}
                        alt="Description"
                        sx={{
                          width: "100%",
                          maxHeight: 150,
                          objectFit: "cover",
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                    )}
                  </Box>
                  <input
                    ref={descriptionImageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleDescriptionImageSelect}
                    disabled={!canEdit || isUpdating || isUploadingDescriptionImage}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    onClick={() => descriptionImageInputRef.current?.click()}
                    disabled={!canEdit || isUpdating || isUploadingDescriptionImage}
                    fullWidth
                    sx={{ textTransform: "none" }}
                  >
                    {isUploadingDescriptionImage
                      ? "Uploading..."
                      : settings.descriptionImageUrl
                      ? "Replace Image"
                      : "Upload Image"}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Image shown in the About section
                  </Typography>
                </Box>

                {/* Store Image Upload */}
                {isAdCenterEnabled && (
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="500"
                      color="text.primary"
                      gutterBottom
                      sx={{ mb: 1.5 }}
                    >
                      Store Image
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      {settings.storeImageUrl && (
                        <Box
                          component="img"
                          src={settings.storeImageUrl}
                          alt="Store"
                          sx={{
                            width: "100%",
                            maxHeight: 150,
                            objectFit: "cover",
                            borderRadius: 1,
                            mb: 1,
                          }}
                        />
                      )}
                    </Box>
                    <input
                      ref={storeImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleStoreImageSelect}
                      disabled={!canEdit || isUpdating || isUploadingStoreImage}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<ImageIcon />}
                      onClick={() => storeImageInputRef.current?.click()}
                      disabled={!canEdit || isUpdating || isUploadingStoreImage}
                      fullWidth
                      sx={{ textTransform: "none" }}
                    >
                      {isUploadingStoreImage
                        ? "Uploading..."
                        : settings.storeImageUrl
                        ? "Replace Image"
                        : "Upload Image"}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Image shown in the Store section
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {isAdCenterEnabled && (
              <>
                <Divider sx={{ my: 1 }} />

                {/* Shop Posts Toggle */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    color="text.primary"
                    gutterBottom
                    sx={{ mb: 2.5, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}
                  >
                    Content
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Switch
                      checked={settings.showShopPosts ?? true}
                      onChange={() => handleToggle("showShopPosts")}
                      disabled={!canEdit || isUpdating}
                      color="primary"
                      size="medium"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        Show Shop Posts
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Display shop posts/advertisements on the public page
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
    </Paper>
  );
};

export default PublicPageSettingsPanel;
